/**
 * contractorEngineConfig.tsx — config silnika formatek dla kartoteki Kontrahent
 * (keystone unifikacja-formatek, ostatni krok migracji — mirror
 * `invoiceEngineConfig.ts` / `contractEngineConfig.ts`).
 *
 * WSZYSTKIE 27 pól core kontrahenta dostaje custom renderer — zero natywnej
 * delegacji do `ExtFieldValueInput` (kontrahent używa `CoreInput`/`Dropdown`/
 * `DatePicker` bezpośrednio, nie generycznego widgetu per fieldType), więc
 * gałąź natywna silnika jest martwa dla tej kartoteki — `labelSource`/
 * `coerceEmptyToNull` zostają kosmetyczne (jak w konfigu faktur/umów/pism).
 *
 * Etykiety: `ctx.field.name` (definicja pola z `GET /system-modules/contractors/fields`,
 * DB-driven), NIE i18n — zachowanie 1:1 z `contractorCoreRenderers.tsx` (przed
 * migracją). `required={ctx.field.isRequired}` — wartość z metadanych (`isRequired: true`
 * w seedzie: `name` i `company_id`).
 *
 * Kanał `extra` (ContractorExtra) niesie stan/callbacki NIP specyficzne dla
 * kontrahenta (onBlur check-nip + MF Biała Lista lookup) — przeniesione z
 * `ContractorDataTab` do `ContractorDetail.tsx` (jedyny stan, który był
 * WCZEŚNIEJ trzymany w komponencie zakładki, nie w rodzicu — silnik wymaga
 * jednego kierunku przepływu: strona → runtime.extra → renderer).
 *
 * `tags`/`pkd_codes` NIE wchodzą do tego configu — TEXT[] outliers bez wpisu
 * w katalogu `system_module_fields` (patrz komentarz w seedzie), zostają
 * hardkodowaną sekcją w `ContractorDataTab.tsx`.
 */

import { useTranslation } from 'react-i18next';
import {
  CONTRACTOR_OBJECT_TYPES,
  CONTRACTOR_STATUSES,
  CONTRACTOR_VAT_STATUSES,
  RECORD_LANGUAGES,
} from '@saas/shared';
import { Search } from 'lucide-react';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { CoreInput, type CoreInputProps } from '@/components/CoreInput';
import { CORE_INPUT_CLASS } from '@/components/coreRendererClasses';
import { DatePicker } from '@/components/DatePicker';
import { Dropdown } from '@/components/Dropdown';
import { GroupsInput } from '@/components/groups/GroupsInput';
import { Field } from '@/components/kartoteki/CoreFieldLayout';
import type {
  CoreFieldConfig,
  FieldRenderContext,
  FieldRenderer,
  KartotekaEngineConfig,
} from '@/components/kartoteki/engine/engine-config';
import { RecordCategoryPicker } from '@/components/record-categories/RecordCategoryPicker';
import { CompanyPicker, type CompanyPickerOption } from '@/components/ui/CompanyPicker';
import { UserPicker } from '@/components/ui/UserPicker';
import {
  contractorObjectTypeLabel,
  contractorStatusLabel,
  contractorVatStatusLabel,
  recordLanguageLabel,
} from '@/lib/core-field-option-labels';
import { cn } from '@/lib/utils';

import type { ContractorFormData } from './ContractorDataTab';
import { CONTRACTOR_CORE_FIELD_SLUGS } from './ContractorDataTab';

/** Ramka błędu — parytet z `CoreInput`'s `INVALID_CLASS` (patrz CoreInput.tsx). */
const INVALID_CLASS = 'border-red-500 ring-1 ring-red-500';

/**
 * Stan runtime specyficzny dla kontrahenta, niesiony przez `FieldRenderContext.extra` /
 * `KartotekaRenderRuntime.extra` (silnik go nie interpretuje — patrz engine-config.ts).
 * Kanał NIP: onBlur check-nip (self-service share modal) + MF Biała Lista lookup —
 * oba żyją w `ContractorDetail.tsx` (przeniesione z `ContractorDataTab`).
 */
export interface ContractorExtra {
  onNipBlur: () => void | Promise<void>;
  onMfLookup: () => void | Promise<void>;
  mfLookupLoading: boolean;
  /**
   * Lista firm tenanta dla renderera `company_id` (CompanyPicker) — patrz `ContractorDetail.tsx`.
   * Opcjonalne (nie `onNipBlur`/`onMfLookup`) — istniejące testy budujące `extra` ręcznie dla
   * INNYCH pól (tax_id) nie muszą go podawać; renderer defensywnie spada na `[]`.
   */
  companies?: CompanyPickerOption[];
}

type Ctx = FieldRenderContext<ContractorFormData, ContractorExtra>;

function TextInput(props: Omit<CoreInputProps, 'variant'>) {
  return <CoreInput {...props} />;
}

// ─── Ground-truth fieldType per slug (z seeda, apps/api/lib/seed-system-modules.ts
// → contractors.coreFields) — używany WYŁĄCZNIE przez fallback statyczny silnika
// (403 na metadanych modułu). Renderer i tak owns widget. ───────────────────────
const CONTRACTOR_FIELD_TYPES: Record<string, string> = {
  // Wielokrotny wybór ze słownika (kolumna TEXT[] kodów) — patrz `groupsRenderer`.
  groups: 'multiselect',
  // Pojedynczy wybór z realnej tabeli `record_categories` — patrz `categoryRenderer`.
  category_id: 'select',
  name: 'varchar',
  company_id: 'reference',
  short_name: 'varchar',
  object_type: 'select',
  tax_id: 'varchar',
  vat_eu: 'varchar',
  regon: 'varchar',
  krs: 'varchar',
  status: 'select',
  email: 'email',
  phone: 'varchar',
  mobile: 'varchar',
  // Serwer NIE waliduje formatu adresu WWW (tylko max 255), więc oś formatu też nie może —
  // 'url' odrzucałoby domeny bez schematu (`www.firma.pl`), które backend przyjmuje, i blokowało
  // zapis. Wizualnie input i tak jest `type="url"`; walidacja formatu = tylko tam, gdzie robi ją serwer.
  website: 'varchar',
  currency_code: 'varchar',
  payment_terms_days: 'integer',
  credit_limit: 'numeric',
  is_vat_payer: 'boolean',
  vat_status: 'select',
  owner_user_id: 'reference',
  assigned_user_id: 'reference',
  source: 'varchar',
  industry: 'varchar',
  preferred_language: 'select',
  // Przechowywane jako pełny ISO datetime (renderer dokleja `T00:00:00.000Z`, serwer trzyma
  // timestamptz `z.string().datetime()`), więc oś formatu MUSI być 'timestamp' — 'date' (ścisłe
  // YYYY-MM-DD) odrzucałoby zapisaną wartość i BLOKOWAŁO zapis każdej karty z datą zgody RODO.
  gdpr_consent_at: 'timestamp',
  gdpr_marketing_consent: 'boolean',
  gdpr_data_retention_until: 'date',
  notes: 'text',
  symfonia_position: 'integer',
  symfonia_active: 'boolean',
  symfonia_external_id: 'varchar',
  sync_excluded: 'boolean',
};

// ─── Helpery sub-komponenty (portowane 1:1 z contractorCoreRenderers.tsx) ──────

function ObjectTypeSelect({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  return (
    <Dropdown
      value={ctx.form.objectType}
      onChange={(v) => ctx.setField({ objectType: v as ContractorFormData['objectType'] })}
      options={CONTRACTOR_OBJECT_TYPES.map((o) => ({
        value: o,
        label: contractorObjectTypeLabel(o, t),
      }))}
      disabled={!ctx.editable}
      className={cn(CORE_INPUT_CLASS, ctx.hasError?.('objectType') && INVALID_CLASS)}
    />
  );
}

function StatusSelect({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  return (
    <Dropdown
      value={ctx.form.status}
      onChange={(v) => ctx.setField({ status: v as ContractorFormData['status'] })}
      options={CONTRACTOR_STATUSES.map((s) => ({
        value: s,
        label: contractorStatusLabel(s, t),
      }))}
      disabled={!ctx.editable}
      className={cn(CORE_INPUT_CLASS, ctx.hasError?.('status') && INVALID_CLASS)}
    />
  );
}

function VatStatusSelect({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  return (
    <Dropdown
      value={ctx.form.vatStatus ?? ''}
      onChange={(v) => ctx.setField({ vatStatus: (v || null) as ContractorFormData['vatStatus'] })}
      options={[
        { value: '', label: '—' },
        ...CONTRACTOR_VAT_STATUSES.map((s) => ({
          value: s,
          label: contractorVatStatusLabel(s, t),
        })),
      ]}
      disabled={!ctx.editable}
      className={cn(CORE_INPUT_CLASS, ctx.hasError?.('vatStatus') && INVALID_CLASS)}
    />
  );
}

function PreferredLanguageSelect({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  return (
    <Dropdown
      value={ctx.form.preferredLanguage}
      onChange={(v) =>
        ctx.setField({ preferredLanguage: v as ContractorFormData['preferredLanguage'] })
      }
      options={RECORD_LANGUAGES.map((code) => ({
        value: code,
        label: recordLanguageLabel(code, t),
      }))}
      disabled={!ctx.editable}
      className={cn(CORE_INPUT_CLASS, ctx.hasError?.('preferredLanguage') && INVALID_CLASS)}
      data-testid="contractor-preferredLanguage"
    />
  );
}

function GdprMarketingConsentField({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  return (
    <Field label={ctx.field.name} colSpan={2} error={ctx.getError?.('gdprMarketingConsent')}>
      <label className="inline-flex items-center gap-2">
        <input
          type="checkbox"
          checked={ctx.form.gdprMarketingConsent}
          onChange={(e) => ctx.setField({ gdprMarketingConsent: e.target.checked })}
          disabled={!ctx.editable}
          className="h-4 w-4 rounded border-border-primary text-accent-600"
          data-testid="contractor-gdprMarketingConsent"
        />
        <span className="text-sm text-text-secondary">
          {t('tenant.contractors.detail.field.gdprMarketingConsentHint')}
        </span>
      </label>
    </Field>
  );
}

// ─── Mapa renderers (slug → FieldRenderer) ─────────────────────────────────────

const nameRenderer: FieldRenderer<ContractorFormData, ContractorExtra> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired} error={ctx.getError?.('name')}>
    <TextInput
      testId="contractor-form-name"
      value={ctx.form.name}
      onChange={(v) => ctx.setField({ name: v })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('name')}
    />
  </Field>
);

const shortNameRenderer: FieldRenderer<ContractorFormData, ContractorExtra> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired} error={ctx.getError?.('shortName')}>
    <TextInput
      value={ctx.form.shortName ?? ''}
      onChange={(v) => ctx.setField({ shortName: v || null })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('shortName')}
    />
  </Field>
);

const objectTypeRenderer: FieldRenderer<ContractorFormData, ContractorExtra> = (ctx) => (
  <Field
    label={ctx.field.name}
    required={ctx.field.isRequired}
    error={ctx.getError?.('objectType')}
  >
    <ObjectTypeSelect ctx={ctx} />
  </Field>
);

/**
 * `tax_id` — jedyne pole z kanałem `extra`: onBlur check-nip (self-service share
 * modal, przeniesiony do ContractorDetail) + przycisk lookup MF Biała Lista.
 */
const taxIdRenderer: FieldRenderer<ContractorFormData, ContractorExtra> = (ctx) => {
  const mfLookupLabel = ctx.t('tenant.contractors.detail.mfLookup.button');
  return (
    <Field label={ctx.field.name} required={ctx.field.isRequired} error={ctx.getError?.('taxId')}>
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <TextInput
            testId="contractor-form-taxId"
            value={ctx.form.taxId ?? ''}
            onChange={(v) => ctx.setField({ taxId: v || null })}
            onBlur={ctx.extra?.onNipBlur}
            disabled={!ctx.editable}
            invalid={ctx.hasError?.('taxId')}
          />
        </div>
        {ctx.editable && ctx.extra?.onMfLookup && (
          <Button
            variant="primary"
            data-testid="contractor-form-mf-lookup"
            onClick={() => void ctx.extra?.onMfLookup?.()}
            disabled={!ctx.form.taxId?.trim()}
            loading={ctx.extra?.mfLookupLoading}
            leftIcon={<Search size={16} aria-hidden="true" />}
            className="whitespace-nowrap"
            title={mfLookupLabel}
          >
            {mfLookupLabel}
          </Button>
        )}
      </div>
    </Field>
  );
};

const vatEuRenderer: FieldRenderer<ContractorFormData, ContractorExtra> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired} error={ctx.getError?.('vatEu')}>
    <TextInput
      value={ctx.form.vatEu ?? ''}
      onChange={(v) => ctx.setField({ vatEu: v || null })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('vatEu')}
    />
  </Field>
);

const regonRenderer: FieldRenderer<ContractorFormData, ContractorExtra> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired} error={ctx.getError?.('regon')}>
    <TextInput
      value={ctx.form.regon ?? ''}
      onChange={(v) => ctx.setField({ regon: v || null })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('regon')}
    />
  </Field>
);

const krsRenderer: FieldRenderer<ContractorFormData, ContractorExtra> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired} error={ctx.getError?.('krs')}>
    <TextInput
      value={ctx.form.krs ?? ''}
      onChange={(v) => ctx.setField({ krs: v || null })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('krs')}
    />
  </Field>
);

const statusRenderer: FieldRenderer<ContractorFormData, ContractorExtra> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired} error={ctx.getError?.('status')}>
    <StatusSelect ctx={ctx} />
  </Field>
);

const emailRenderer: FieldRenderer<ContractorFormData, ContractorExtra> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired} error={ctx.getError?.('email')}>
    <TextInput
      type="email"
      value={ctx.form.email ?? ''}
      onChange={(v) => ctx.setField({ email: v || null })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('email')}
    />
  </Field>
);

const phoneRenderer: FieldRenderer<ContractorFormData, ContractorExtra> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired} error={ctx.getError?.('phone')}>
    <TextInput
      value={ctx.form.phone ?? ''}
      onChange={(v) => ctx.setField({ phone: v || null })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('phone')}
    />
  </Field>
);

const mobileRenderer: FieldRenderer<ContractorFormData, ContractorExtra> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired} error={ctx.getError?.('mobile')}>
    <TextInput
      value={ctx.form.mobile ?? ''}
      onChange={(v) => ctx.setField({ mobile: v || null })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('mobile')}
    />
  </Field>
);

const websiteRenderer: FieldRenderer<ContractorFormData, ContractorExtra> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired} error={ctx.getError?.('website')}>
    <TextInput
      value={ctx.form.website ?? ''}
      onChange={(v) => ctx.setField({ website: v || null })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('website')}
    />
  </Field>
);

// Mimo typu `string` (niepusty) w `ContractorFormData`, backend potrafi zwrócić `null`
// dla kontrahentów zsynchronizowanych z Symfonii bez waluty (kolumna DB jest nullable) —
// `value={... ?? ''}` trzyma input kontrolowany od pierwszego renderu (inaczej React
// ostrzega "changing an uncontrolled input to be controlled"). Stan `form` i payload
// zapisu NIE są dotykane (`?? ''` jest tylko w warstwie wyświetlania).
const currencyCodeRenderer: FieldRenderer<ContractorFormData, ContractorExtra> = (ctx) => (
  <Field
    label={ctx.field.name}
    required={ctx.field.isRequired}
    error={ctx.getError?.('currencyCode')}
  >
    <TextInput
      testId="contractor-form-currencyCode"
      value={ctx.form.currencyCode ?? ''}
      onChange={(v) => ctx.setField({ currencyCode: v.toUpperCase() })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('currencyCode')}
    />
  </Field>
);

const paymentTermsDaysRenderer: FieldRenderer<ContractorFormData, ContractorExtra> = (ctx) => (
  <Field
    label={ctx.field.name}
    required={ctx.field.isRequired}
    error={ctx.getError?.('paymentTermsDays')}
  >
    <TextInput
      type="number"
      value={ctx.form.paymentTermsDays?.toString() ?? ''}
      onChange={(v) => ctx.setField({ paymentTermsDays: v ? Number(v) : null })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('paymentTermsDays')}
    />
  </Field>
);

const creditLimitRenderer: FieldRenderer<ContractorFormData, ContractorExtra> = (ctx) => (
  <Field
    label={ctx.field.name}
    required={ctx.field.isRequired}
    error={ctx.getError?.('creditLimit')}
  >
    <TextInput
      type="number"
      value={ctx.form.creditLimit ?? ''}
      onChange={(v) => ctx.setField({ creditLimit: v || null })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('creditLimit')}
    />
  </Field>
);

const isVatPayerRenderer: FieldRenderer<ContractorFormData, ContractorExtra> = (ctx) => (
  <Field label={ctx.field.name} error={ctx.getError?.('isVatPayer')}>
    <input
      type="checkbox"
      checked={ctx.form.isVatPayer ?? false}
      onChange={(e) => ctx.setField({ isVatPayer: e.target.checked })}
      disabled={!ctx.editable}
      className="h-4 w-4"
    />
  </Field>
);

const vatStatusRenderer: FieldRenderer<ContractorFormData, ContractorExtra> = (ctx) => (
  <Field label={ctx.field.name} error={ctx.getError?.('vatStatus')}>
    <VatStatusSelect ctx={ctx} />
  </Field>
);

// ─── Integracja Symfonia (F18/FK) — 3 pola, wcześniej zaszyty baner/checkbox ──────
// Pozycja + status = READ-ONLY (silnik wymusza `ctx.editable=false` z flagi katalogu
// `read_only`, więc `disabled={!ctx.editable}` wychodzi zawsze true). Wyłączenie z
// synchronizacji = EDYTOWALNE (przełącznik usera). Etykiety z `ctx.field.name` (seed).
//
// Pozycja/status są PER FIRMA (ADR-0039) — do etykiety doklejamy nazwę firmy, dla której GET
// zwrócił wartość (aktywny X-Company-Id), żeby było jasne, której firmy dotyczy. Nazwa z
// `ctx.extra.companies` (ta sama lista co CompanyPicker), po `ctx.form.symfoniaCompanyId`.

function symfoniaCompanyName(ctx: Ctx): string | null {
  const id = ctx.form.symfoniaCompanyId;
  if (!id) return null;
  return ctx.extra?.companies?.find((c) => c.id === id)?.legalName ?? null;
}

function symfoniaLabel(ctx: Ctx): string {
  const company = symfoniaCompanyName(ctx);
  return company ? `${ctx.field.name} — ${company}` : ctx.field.name;
}

const symfoniaPositionRenderer: FieldRenderer<ContractorFormData, ContractorExtra> = (ctx) => (
  <Field label={symfoniaLabel(ctx)}>
    <TextInput
      type="number"
      value={ctx.form.symfoniaPosition?.toString() ?? ''}
      onChange={() => {}}
      disabled={!ctx.editable}
      data-testid="contractor-symfoniaPosition"
    />
  </Field>
);

// ID rekordu w Symfonii (external_id z powiązania) — READ-ONLY, ODRĘBNE od Pozycji. Per firma
// (etykieta z nazwą firmy jak przy Pozycji). Tekst, bo Id może nie być czysto numeryczne.
const symfoniaExternalIdRenderer: FieldRenderer<ContractorFormData, ContractorExtra> = (ctx) => (
  <Field label={symfoniaLabel(ctx)}>
    <TextInput
      value={ctx.form.symfoniaExternalId ?? ''}
      onChange={() => {}}
      disabled={!ctx.editable}
      data-testid="contractor-symfoniaExternalId"
    />
  </Field>
);

const symfoniaActiveRenderer: FieldRenderer<ContractorFormData, ContractorExtra> = (ctx) => {
  const active = ctx.form.symfoniaActive;
  const label =
    active == null
      ? ctx.t('tenant.contractors.detail.symfonia.unknown')
      : active
        ? ctx.t('tenant.contractors.detail.symfonia.active')
        : ctx.t('tenant.contractors.detail.symfonia.blocked');
  return (
    <Field label={symfoniaLabel(ctx)}>
      <span
        className={cn(
          'inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium',
          active === false
            ? 'bg-red-500/10 text-red-700 dark:text-red-400'
            : active
              ? 'bg-green-500/10 text-green-700 dark:text-green-400'
              : 'bg-bg-tertiary text-text-tertiary',
        )}
        data-testid="contractor-symfoniaActive"
      >
        {label}
      </span>
    </Field>
  );
};

const syncExcludedRenderer: FieldRenderer<ContractorFormData, ContractorExtra> = (ctx) => (
  <Field label={ctx.field.name} colSpan={2}>
    <Checkbox
      checked={ctx.form.syncExcluded}
      onChange={(e) => ctx.setField({ syncExcluded: e.target.checked })}
      disabled={!ctx.editable}
      label={ctx.t('tenant.contractors.detail.field.syncExcludedHint')}
      data-testid="contractor-syncExcluded"
    />
  </Field>
);

/**
 * Firma-właściciel (`home_company_id`) — WYŁĄCZNIE pole tworzenia (mirror
 * `employeeEngineConfig.tsx`'s `company_id`, ale `disabled` liczone od `isCreate`, nie od
 * `editable`): po zapisaniu kontrahenta zmiana firmy idzie przez osobny, świadomie
 * odseparowany flow transferu właścicielstwa (zakładka "Dostępy firm"), nie przez to pole —
 * `PATCH /contractors/:id` odrzuca `homeCompanyId` kodem `USE_TRANSFER_OWNERSHIP_ENDPOINT`.
 */
const companyIdRenderer: FieldRenderer<ContractorFormData, ContractorExtra> = (ctx) => (
  <Field
    label={ctx.field.name}
    required={ctx.field.isRequired}
    error={ctx.getError?.('targetCompanyId')}
  >
    <CompanyPicker
      value={ctx.form.targetCompanyId || null}
      onChange={(v) => ctx.setField({ targetCompanyId: v ?? '' })}
      companies={ctx.extra?.companies ?? []}
      disabled={!ctx.isCreate}
      required={ctx.field.isRequired}
      testId="contractor-targetCompanyId"
    />
  </Field>
);

const ownerUserIdRenderer: FieldRenderer<ContractorFormData, ContractorExtra> = (ctx) => (
  <Field label={ctx.field.name} error={ctx.getError?.('ownerUserId')}>
    <UserPicker
      value={ctx.form.ownerUserId}
      onChange={(v) => ctx.setField({ ownerUserId: v })}
      companyId={ctx.companyId}
      disabled={!ctx.editable}
      data-testid="contractor-ownerUserId"
    />
  </Field>
);

const assignedUserIdRenderer: FieldRenderer<ContractorFormData, ContractorExtra> = (ctx) => (
  <Field label={ctx.field.name} error={ctx.getError?.('assignedUserId')}>
    <UserPicker
      value={ctx.form.assignedUserId}
      onChange={(v) => ctx.setField({ assignedUserId: v })}
      companyId={ctx.companyId}
      disabled={!ctx.editable}
      data-testid="contractor-assignedUserId"
    />
  </Field>
);

const sourceRenderer: FieldRenderer<ContractorFormData, ContractorExtra> = (ctx) => (
  <Field label={ctx.field.name} error={ctx.getError?.('source')}>
    <TextInput
      value={ctx.form.source ?? ''}
      onChange={(v) => ctx.setField({ source: v || null })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('source')}
    />
  </Field>
);

const industryRenderer: FieldRenderer<ContractorFormData, ContractorExtra> = (ctx) => (
  <Field label={ctx.field.name} error={ctx.getError?.('industry')}>
    <TextInput
      value={ctx.form.industry ?? ''}
      onChange={(v) => ctx.setField({ industry: v || null })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('industry')}
    />
  </Field>
);

const preferredLanguageRenderer: FieldRenderer<ContractorFormData, ContractorExtra> = (ctx) => (
  <Field label={ctx.field.name} error={ctx.getError?.('preferredLanguage')}>
    <PreferredLanguageSelect ctx={ctx} />
  </Field>
);

const notesRenderer: FieldRenderer<ContractorFormData, ContractorExtra> = (ctx) => (
  <Field label={ctx.field.name} colSpan={2} error={ctx.getError?.('notes')}>
    <textarea
      value={ctx.form.notes ?? ''}
      onChange={(e) => ctx.setField({ notes: e.target.value || null })}
      disabled={!ctx.editable}
      rows={4}
      className={cn(CORE_INPUT_CLASS, ctx.hasError?.('notes') && INVALID_CLASS)}
    />
  </Field>
);

const gdprConsentAtRenderer: FieldRenderer<ContractorFormData, ContractorExtra> = (ctx) => (
  <Field label={ctx.field.name} error={ctx.getError?.('gdprConsentAt')}>
    <DatePicker
      value={ctx.form.gdprConsentAt ? ctx.form.gdprConsentAt.slice(0, 10) : ''}
      onChange={(v) => ctx.setField({ gdprConsentAt: v ? `${v}T00:00:00.000Z` : null })}
      disabled={!ctx.editable}
      data-testid="contractor-gdprConsentAt"
    />
  </Field>
);

const gdprDataRetentionUntilRenderer: FieldRenderer<ContractorFormData, ContractorExtra> = (
  ctx,
) => (
  <Field label={ctx.field.name} error={ctx.getError?.('gdprDataRetentionUntil')}>
    <DatePicker
      value={ctx.form.gdprDataRetentionUntil ?? ''}
      onChange={(v) => ctx.setField({ gdprDataRetentionUntil: v || null })}
      disabled={!ctx.editable}
      data-testid="contractor-gdprDataRetentionUntil"
    />
  </Field>
);

const gdprMarketingConsentRenderer: FieldRenderer<ContractorFormData, ContractorExtra> = (ctx) => (
  <GdprMarketingConsentField ctx={ctx} />
);

/**
 * Grupy ze słownika `contractor_groups` — wielokrotny wybór (dropdown z checkboxami).
 * Renderer siedzi TUTAJ, a nie w hardkodowanej sekcji zakładki, żeby o miejscu pola na
 * formatce decydował LAYOUT z buildera (pole jest w palecie „Pola do dodania").
 * Migracja z `contractorCoreRenderers.tsx` (plik usunięty przy przejściu na silnik).
 */
const groupsRenderer: FieldRenderer<ContractorFormData, ContractorExtra> = (ctx) => (
  <Field label={ctx.field.name} colSpan={2}>
    <GroupsInput
      dictionaryCode="contractor_groups"
      value={ctx.form.groups}
      onChange={(next) => ctx.setField({ groups: next })}
      disabled={!ctx.editable}
      data-testid="contractor-groups"
    />
  </Field>
);

/**
 * Kategoria z realnej tabeli `record_categories` (kartoteka_key='contractors') — pojedynczy
 * wybór, DYSTYNKTYWNY od `groupsRenderer` (wiele, słownik). `RecordCategoryPicker` rysuje
 * własny `<Field>` (mirror `SystemModuleCategoryField`), więc renderer nie owija go drugi raz.
 * Migracja 0123 — patrz `apps/api/lib/records/categories.ts`.
 */
const categoryRenderer: FieldRenderer<ContractorFormData, ContractorExtra> = (ctx) => (
  <RecordCategoryPicker
    kartotekaKey="contractors"
    value={ctx.form.categoryId}
    onChange={(next) => ctx.setField({ categoryId: next })}
    editable={ctx.editable}
    testId="contractor-category"
  />
);

const CONTRACTOR_RENDERERS: Record<string, FieldRenderer<ContractorFormData, ContractorExtra>> = {
  groups: groupsRenderer,
  category_id: categoryRenderer,
  name: nameRenderer,
  company_id: companyIdRenderer,
  short_name: shortNameRenderer,
  object_type: objectTypeRenderer,
  tax_id: taxIdRenderer,
  vat_eu: vatEuRenderer,
  regon: regonRenderer,
  krs: krsRenderer,
  status: statusRenderer,
  email: emailRenderer,
  phone: phoneRenderer,
  mobile: mobileRenderer,
  website: websiteRenderer,
  currency_code: currencyCodeRenderer,
  payment_terms_days: paymentTermsDaysRenderer,
  credit_limit: creditLimitRenderer,
  is_vat_payer: isVatPayerRenderer,
  vat_status: vatStatusRenderer,
  owner_user_id: ownerUserIdRenderer,
  assigned_user_id: assignedUserIdRenderer,
  source: sourceRenderer,
  industry: industryRenderer,
  preferred_language: preferredLanguageRenderer,
  notes: notesRenderer,
  gdpr_consent_at: gdprConsentAtRenderer,
  gdpr_data_retention_until: gdprDataRetentionUntilRenderer,
  gdpr_marketing_consent: gdprMarketingConsentRenderer,
  symfonia_position: symfoniaPositionRenderer,
  symfonia_active: symfoniaActiveRenderer,
  symfonia_external_id: symfoniaExternalIdRenderer,
  sync_excluded: syncExcludedRenderer,
};

const fields: Record<string, CoreFieldConfig<ContractorFormData, ContractorExtra>> = {};
for (const slug of Object.keys(CONTRACTOR_CORE_FIELD_SLUGS)) {
  const stateKey = CONTRACTOR_CORE_FIELD_SLUGS[slug]!;
  fields[slug] = {
    stateKey,
    fieldType: CONTRACTOR_FIELD_TYPES[slug] ?? 'varchar',
    renderer: CONTRACTOR_RENDERERS[slug]!,
    // Jedyne dwa pola z colSpan=2 w oryginalnych renderach (notes/gdpr_marketing_consent
    // rysują go zresztą same na swoim <Field> — wpis tu jest kosmetyczny, patrz komentarz
    // nagłówka pliku, gałąź natywna silnika jest martwa dla tej kartoteki).
    ...(slug === 'notes' || slug === 'gdpr_marketing_consent' || slug === 'groups'
      ? { colSpan: 2 as const }
      : {}),
  };
}

export const contractorEngineConfig: KartotekaEngineConfig<ContractorFormData, ContractorExtra> = {
  moduleSlug: 'contractors',
  // LOAD-BEARING: engine's ext delegation emits `${testIdPrefix}-${slug}` — testid
  // `contractor-ext-notes_ext` (ContractorDetail.test.tsx) wymaga DOKŁADNIE tego prefiksu.
  testIdPrefix: 'contractor-ext',
  coreFieldSlugs: Object.keys(CONTRACTOR_CORE_FIELD_SLUGS),
  fields,
  coerceEmptyToNull: false,
};
